import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { completePendingGoogleRedirect } from '../../services/authProviders';
import { fetchEventByCode, resumeEventByCode, RESUME_STATUS } from '../../services/eventLifecycle';
import { syncProfileAfterAuthChange } from '../../services/authSession';
import { LINK_OUTCOME } from '../../utils/authIdentity';
import { IN_PARTY_GOOGLE_ACTION, resolveInPartyGoogleAuth } from '../../utils/inPartyGoogleAuth';
import { cleanupStores } from '../../utils/storeUtils';

/**
 * Completes a Firebase redirect that started because popup Google auth was
 * blocked. Restores /party/:eventCode for a same-uid link; signedInInstead
 * still cannot bypass approval.
 */
export default function GoogleRedirectHandler() {
  const navigate = useNavigate();
  const ranRef = React.useRef(false);

  React.useEffect(() => {
    if (ranRef.current) return undefined;
    ranRef.current = true;
    let cancelled = false;

    (async () => {
      const result = await completePendingGoogleRedirect();
      if (cancelled || !result) return;

      if (result.outcome === LINK_OUTCOME.redirectStarted) return;
      if (result.user) {
        await syncProfileAfterAuthChange(result.user);
      }

      const eventCode = result.pending?.eventCode;
      if (!eventCode) {
        if (result.pending?.returnPath) navigate(result.pending.returnPath, { replace: true });
        return;
      }

      if (result.outcome === LINK_OUTCOME.signedInInstead) {
        const eventData = await fetchEventByCode(eventCode);
        const decision = resolveInPartyGoogleAuth({
          outcome: result.outcome,
          nextUid: result.user?.uid,
          eventData,
        });
        if (decision.action === IN_PARTY_GOOGLE_ACTION.rejoin) {
          toast('That Google account is a different Rhythmé identity. Join this party again to continue.');
          cleanupStores();
          navigate(`/join/${eventCode}`, { replace: true });
          return;
        }
      }

      if (result.outcome === LINK_OUTCOME.failed || result.outcome === LINK_OUTCOME.cancelled) {
        if (result.error?.message) toast.error(result.error.message);
        return;
      }

      const resume = await resumeEventByCode(eventCode);
      if (resume?.status === RESUME_STATUS.left || resume?.status === RESUME_STATUS.notParticipant) {
        navigate(`/join/${eventCode}`, { replace: true });
        return;
      }
      navigate(`/party/${eventCode}`, { replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return null;
}
